/**
 * CREATIONS PAGE
 * 
 * Gallery of things you've made using your collection items:
 * - Manicures (for usage tracking)
 * - Press-on sets (for sale)
 * - Jewelry (for sale)
 * - Other crafts
 * - Glassmorphic styling
 */

import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, deleteDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { Plus, Search, Grid, List, Package, Palette } from 'lucide-react';
import type { Creation } from '../types/creationTypes';
import AddCreationModal from '../components/creations/AddCreationModal';
import CreationCard from '../components/creations/CreationCard';
import CreationDetailModal from '../components/creations/CreationDetailModal';
import PageVisibilityBanner from '../components/PageVisibilityBanner';
import { PageHeader } from '../components/common';

const Creations: React.FC = () => {
  const [creations, setCreations] = useState<Creation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCreation, setSelectedCreation] = useState<Creation | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [marketplaceFilter, setMarketplaceFilter] = useState<'all' | 'listed' | 'unlisted'>('all');
  
  // User profile for visibility banner
  const [username, setUsername] = useState<string>('');
  const [isCreationsPublic, setIsCreationsPublic] = useState<boolean>(false);

  // Load creations
  const loadCreations = async (userId: string) => {
    try {
      setLoading(true);
      const creationsRef = collection(db, 'users', userId, 'creations');
      const snapshot = await getDocs(creationsRef);
      
      const loadedCreations: Creation[] = [];
      snapshot.forEach((doc) => {
        loadedCreations.push({ id: doc.id, ...doc.data() } as Creation);
      });
      
      // Sort by dateCreated (newest first), fall back to createdAt for items without dateCreated
      loadedCreations.sort((a, b) => {
        const dateA = a.dateCreated || (a.createdAt?.toDate?.()?.toISOString?.().split('T')[0]) || '1970-01-01';
        const dateB = b.dateCreated || (b.createdAt?.toDate?.()?.toISOString?.().split('T')[0]) || '1970-01-01';
        return dateB.localeCompare(dateA); // Descending (newest first)
      });
      
      setCreations(loadedCreations);
    } catch (error) {
      console.error('Error loading creations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        loadCreations(user.uid);
        
        // Fetch user profile for visibility banner
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUsername(data.username || data.profile?.username || '');
            setIsCreationsPublic(data.privacy?.showCreations !== false);
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
        }
      } else {
        setCreations([]);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Filter creations
  const filteredCreations = creations.filter(creation => {
    // Search filter
    const matchesSearch = !searchQuery || 
      creation.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      creation.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Category filter
    const matchesCategory = categoryFilter === 'all' || 
      creation.marketplaceCategory === categoryFilter ||
      (categoryFilter === 'mani' && !creation.marketplaceCategory);
    
    // Marketplace filter
    const matchesMarketplace = marketplaceFilter === 'all' ||
      (marketplaceFilter === 'listed' && creation.listedOnMarketplace) ||
      (marketplaceFilter === 'unlisted' && !creation.listedOnMarketplace);
    
    return matchesSearch && matchesCategory && matchesMarketplace;
  });

  // Stats
  const totalCreations = creations.length;
  const listedCount = creations.filter(c => c.listedOnMarketplace).length;
  const pressOnCount = creations.filter(c => c.marketplaceCategory === 'pressons').length;
  const jewelryCount = creations.filter(c => c.marketplaceCategory === 'jewelry').length;

  // Handle creation saved
  const handleCreationSaved = () => {
    const user = auth.currentUser;
    if (user) {
      loadCreations(user.uid);
    }
    setShowAddModal(false);
  };

  // Handle delete
  const handleDelete = async (creationId: string) => {
    if (!confirm('Are you sure you want to delete this creation?')) return;
    
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      await deleteDoc(doc(db, 'users', user.uid, 'creations', creationId));
      setCreations(prev => prev.filter(c => c.id !== creationId));
      setSelectedCreation(null);
    } catch (error) {
      console.error('Error deleting creation:', error);
      alert('Error deleting creation. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 border-4 border-slate-600 border-t-slate-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-glass-secondary font-body">Loading your creations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 pb-8">
      {/* Visibility Banner */}
      {username && (
        <PageVisibilityBanner 
          isPublic={isCreationsPublic}
          publicPath={`/creations/${username}`}
          pageType="creations"
          className="mb-4"
        />
      )}

      {/* Header */}
      <PageHeader
        icon={Palette}
        title="My Creations"
        subtitle="Track your manis and list items for sale"
        className="mb-6"
        actions={
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white rounded-full font-bold hover:from-fuchsia-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl font-body"
          >
            <Plus className="icon-md" />
            Add Creation
          </button>
        }
      >
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="glass-stats p-4 text-center border border-pink-200/50">
            <p className="text-3xl font-bold text-pink-600">{totalCreations}</p>
            <p className="text-sm text-pink-500 font-body">Total Creations</p>
          </div>
          <div className="glass-stats p-4 text-center border border-green-200/50">
            <p className="text-3xl font-bold text-green-600">{listedCount}</p>
            <p className="text-sm text-green-500 font-body">Listed for Sale</p>
          </div>
          <div className="glass-stats p-4 text-center border border-purple-200/50">
            <p className="text-3xl font-bold text-purple-600">{pressOnCount}</p>
            <p className="text-sm text-purple-500 font-body">Press-On Sets</p>
          </div>
          <div className="glass-stats p-4 text-center border border-amber-200/50">
            <p className="text-3xl font-bold text-amber-600">{jewelryCount}</p>
            <p className="text-sm text-amber-500 font-body">Jewelry Pieces</p>
          </div>
        </div>
      </PageHeader>

      {/* Filters */}
      <div className="mb-6">
        <div className="glass-card p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-muted" />
              <input
                type="text"
                placeholder="Search creations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent bg-white/80 font-body"
              />
            </div>

            {/* Category filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 bg-white/80 font-body"
            >
              <option value="all">All Types</option>
              <option value="mani">Manicures</option>
              <option value="pressons">Press-On Sets</option>
              <option value="jewelry">Jewelry</option>
              <option value="other">Other</option>
            </select>

            {/* Marketplace filter */}
            <select
              value={marketplaceFilter}
              onChange={(e) => setMarketplaceFilter(e.target.value as any)}
              className="px-4 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 bg-white/80 font-body"
            >
              <option value="all">All Status</option>
              <option value="listed">Listed for Sale</option>
              <option value="unlisted">Not Listed</option>
            </select>

            {/* View toggle */}
            <div className="flex items-center gap-1 bg-slate-800/60 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-default ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-slate-800/60'}`}
              >
                <Grid className="icon-md" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-default ${viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-slate-800/60'}`}
              >
                <List className="icon-md" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Creations Grid/List */}
      <div>
        {filteredCreations.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <div className="w-24 h-24 glass-stats rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-600/50">
              <Package className="icon-2xl text-glass-secondary" />
            </div>
            <h3 className="text-xl font-bold text-glass-secondary mb-2 font-heading">
              {creations.length === 0 ? 'No creations yet!' : 'No matching creations'}
            </h3>
            <p className="text-glass-secondary mb-6 font-body">
              {creations.length === 0 
                ? 'Start documenting your nail art and crafts!'
                : 'Try adjusting your filters'}
            </p>
            {creations.length === 0 && (
              <button
                onClick={() => setShowAddModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white rounded-full font-bold hover:from-fuchsia-600 hover:to-purple-600 transition-all font-body"
              >
                <Plus className="icon-md inline mr-2" />
                Create Your First
              </button>
            )}
          </div>
        ) : (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' 
            : 'space-y-4'
          }>
            {filteredCreations.map((creation) => (
              <CreationCard
                key={creation.id}
                creation={creation}
                viewMode={viewMode}
                onClick={() => setSelectedCreation(creation)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Creation Modal */}
      {showAddModal && (
        <AddCreationModal
          onClose={() => setShowAddModal(false)}
          onSave={handleCreationSaved}
        />
      )}

      {/* Creation Detail Modal */}
      {selectedCreation && (
        <CreationDetailModal
          creation={selectedCreation}
          onClose={() => setSelectedCreation(null)}
          onUpdate={handleCreationSaved}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default Creations;
